import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { NotebookText, Loader2, X } from 'lucide-react';
import { notesApi } from '../../lib/api';

const NoteContentView = React.lazy(() => import('./NoteContentView'));

type LinkedPage = { id: string; title: string; notebook_id: string; notebook_name: string };

const useLinkedPages = (kind: 'appointment' | 'event', id: string) => {
    const [pages, setPages] = React.useState<LinkedPage[] | null>(null);
    React.useEffect(() => {
        let alive = true;
        setPages(null);
        notesApi.getLinkedPages(kind, id)
            .then((r: any) => { if (alive) setPages(r.pages || []); })
            .catch(() => { if (alive) setPages([]); });
        return () => { alive = false; };
    }, [kind, id]);
    return pages;
};

/** Une note attachée : titre + contenu réel (pas seulement une référence) — signalé par Emeni le
 *  17/08 après avoir vu la fenêtre rapide ne montrer qu'un titre. Charge son propre contenu, un
 *  appel par page (il y en a rarement plus d'une ou deux attachées à un même RDV/événement). */
function LinkedPageEntry({ page }: { page: LinkedPage }) {
    const [content, setContent] = React.useState<any>(undefined);
    React.useEffect(() => {
        let alive = true;
        notesApi.getPageContent(page.id).then((r: any) => { if (alive) setContent(r.content); }).catch(() => { if (alive) setContent(null); });
        return () => { alive = false; };
    }, [page.id]);

    return (
        <div className="py-1.5 border-b border-border last:border-b-0">
            <p className="text-sm font-bold text-content truncate">
                {page.title} <span className="font-normal text-content-muted text-xs">— {page.notebook_name}</span>
            </p>
            <div className="mt-1">
                {content === undefined ? (
                    <div className="flex items-center gap-1.5 text-xs text-content-muted"><Loader2 size={11} className="animate-spin" /> Chargement…</div>
                ) : (
                    <React.Suspense fallback={<div className="flex items-center gap-1.5 text-xs text-content-muted"><Loader2 size={11} className="animate-spin" /> Chargement…</div>}>
                        <NoteContentView content={content} />
                    </React.Suspense>
                )}
            </div>
        </div>
    );
}

/**
 * Sens inverse du rattachement Notes ↔ RDV/événement (voir NotesModule.tsx `LinkDialog`) : affiche,
 * dans la fiche d'un rendez-vous ou d'un événement, les notes qui y sont attachées — titre et
 * contenu réel. Lecture seule — pour créer/retirer un lien, ou modifier le contenu, on passe par
 * le module Notes lui-même. Ne montre que ce que l'utilisateur courant peut déjà voir (propriétaire
 * ou bloc-notes partagé avec lui) : le rattachement d'une note à un RDV ne la rend pas visible à
 * tout l'établissement.
 */
export const LinkedNotesPanel: React.FC<{ kind: 'appointment' | 'event'; id: string }> = ({ kind, id }) => {
    const pages = useLinkedPages(kind, id);

    if (pages === null) {
        return <div className="flex items-center gap-1.5 text-xs text-content-muted"><Loader2 size={12} className="animate-spin" /> Notes…</div>;
    }
    if (pages.length === 0) return null;

    return (
        <div className="space-y-1.5 p-3 rounded-xl bg-surface-soft border border-border">
            <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5">
                <NotebookText size={12} /> Notes attachées
            </p>
            <div>
                {pages.map((p) => <LinkedPageEntry key={p.id} page={p} />)}
            </div>
        </div>
    );
};

/**
 * Même contenu que `LinkedNotesPanel`, mais en petite fenêtre autonome ouverte directement depuis
 * la bulle du planning (RDV Pro ou Événementiel) — sans passer par la fiche complète. Demandé par
 * Emeni le 2026-08-17 : cliquer l'icône sur la bulle doit montrer les notes rattachées sans détour.
 */
export const LinkedNotesQuickModal: React.FC<{ kind: 'appointment' | 'event'; id: string; label: string; onClose: () => void }> = ({ kind, id, label, onClose }) => {
    const pages = useLinkedPages(kind, id);
    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-surface border border-border rounded-2xl w-full max-w-md max-h-[80vh] shadow-2xl overflow-hidden flex flex-col">
                    <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                        <h3 className="font-bold text-content flex items-center gap-2 min-w-0"><NotebookText size={18} className="shrink-0" /> <span className="truncate">Notes — {label}</span></h3>
                        <button onClick={onClose} className="text-content-muted hover:text-content shrink-0"><X size={18} /></button>
                    </div>
                    <div className="p-5 space-y-2 overflow-y-auto">
                        {pages === null ? (
                            <div className="flex justify-center py-4"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
                        ) : pages.length === 0 ? (
                            <p className="text-sm text-content-muted">Aucune note rattachée.</p>
                        ) : (
                            pages.map((p) => <LinkedPageEntry key={p.id} page={p} />)
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default LinkedNotesPanel;
